/**
 * Subscription Customizer – Seal Subscriptions integration.
 *
 * References:
 *  - Manual installation: https://www.sealsubscriptions.com/articles/manual-installation
 *  - Merchant JS API:     https://www.sealsubscriptions.com/articles/merchant-javascript-api-documentation
 *
 * How Seal works:
 *  1. Add <div class="sealsubs-target-element" data-handle="..." data-product="..."> inside the <form>.
 *  2. After updating the data attributes, call SealSubs.refresh().
 *  3. Seal will inject the subscription widget into the target element.
 *  4. Listen for: sealsubs:subscription_widget_created, sealsubs:selling_plan_changed, sealsubs:price_update.
 *  5. On cart-add, include all <input> / <select> values from the widget in the request.
 *
 * Flow on this page:
 *  - Page loads:  no coffee selected → Seal target is empty → widget does NOT load yet.
 *  - User selects a coffee → set target attributes → call SealSubs.refresh() → widget loads.
 */
(function () {
  'use strict';

  // ── Utilities ─────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function findSizeOption(product) {
    if (!product || !product.options || !product.options.length) return null;
    for (var i = 0; i < product.options.length; i++) {
      if ((product.options[i].name || '').toLowerCase() === 'size') return product.options[i];
    }
    return product.options[0] || null;
  }

  /** True when size option represents 5 lb (handles "5lb", "5 lb", "5 lb (2.27kg)", etc.). */
  function isFiveLbSize(sizeValue) {
    if (!sizeValue || typeof sizeValue !== 'string') return false;
    var s = sizeValue.toLowerCase().trim();
    // Must start with 5 lb / 5lb / 5lbs so "25 lb" or "15lb" do not match.
    return /^5\s*-?\s*lb(s)?\b/.test(s);
  }

  function findVariant(product, sizeValue, grindValue) {
    if (!product || !product.variants || !product.variants.length) return null;
    var opts = product.options || [];
    var sizeIdx = 0;
    var grindIdx = -1;
    for (var i = 0; i < opts.length; i++) {
      var n = (opts[i].name || '').toLowerCase();
      if (n === 'size')  sizeIdx  = i;
      if (n === 'grind') grindIdx = i;
    }
    for (var v = 0; v < product.variants.length; v++) {
      var variant = product.variants[v];
      var o = [
        (variant.option1 || '').trim(),
        (variant.option2 || '').trim(),
        (variant.option3 || '').trim()
      ];
      var sizeMatch  = !sizeValue  || o[sizeIdx] === sizeValue;
      var grindMatch = !grindValue || grindIdx < 0 || o[grindIdx] === grindValue;
      if (sizeMatch && grindMatch) return variant;
    }
    return product.variants[0] || null;
  }

  // ── Section init ──────────────────────────────────────────────────────────

  function initSection(sectionEl) {
    var sectionId = sectionEl.getAttribute('data-section-id');
    if (!sectionId) return;

    // DOM refs
    var coffeeList    = sectionEl.querySelector('[data-coffee-list]');
    var sizeList      = sectionEl.querySelector('[data-size-list]');
    var quantityInput = sectionEl.querySelector('[data-subscription-quantity]');
    var grindSelect   = sectionEl.querySelector('[data-grind-select]');
    var grindStepEl   = sectionEl.querySelector('[data-grind-step]');
    var frequencyHeadingEl = sectionEl.querySelector('[data-frequency-step-heading]');
    var frequencyList = sectionEl.querySelector('[data-frequency-list]');
    var form          = sectionEl.querySelector('[data-subscription-form]');
    var variantInput  = form && form.querySelector('[data-form-variant-id]');
    var qtyFormInput  = form && form.querySelector('[data-form-quantity]');
    var grindFormInput = form && form.querySelector('[data-form-grind]');
    var submitBtn     = sectionEl.querySelector('[data-subscription-submit]');

    // Seal – manual widget
    var sealWrapper = sectionEl.querySelector('[data-seal-widget]');
    var sealTarget  = sealWrapper && sealWrapper.querySelector('.sealsubs-target-element');
    var useSeal     = !!(sealWrapper && sealTarget);

    // App blocks – one per product added via theme editor
    var appBlocks = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-app-block]'));

    // Product data (injected as JSON by Liquid)
    var jsonEl   = document.querySelector('[data-subscription-products-json]');
    var products = [];
    try { if (jsonEl && jsonEl.textContent) products = JSON.parse(jsonEl.textContent); } catch (e) {}

    console.log('[SubCustomizer] init', {
      sectionId    : sectionId,
      useSeal      : useSeal,
      appBlocks    : appBlocks.length,
      SealSubs     : typeof window.SealSubs,
      products     : products.length,
      sealTarget   : !!sealTarget,
      form         : !!form
    });

    // State
    var state = {
      product       : null,
      variant       : null,
      quantity      : 1,
      sellingPlanId : '',
      frequencyLabel: ''
    };

    // Guard to prevent double-loading the same product+variant
    var sealLoadKey = null;
    // sealTarget is declared above; it gets reassigned to the fresh DOM node on each loadSealWidget() call

    // ── Seal helpers ─────────────────────────────────────────────────────

    /**
     * Replace the sealsubs-target-element with a fresh node (so Seal's MutationObserver
     * always sees a brand-new element), set all required attributes, then call SealSubs.refresh().
     *
     * Key insight: Seal may skip re-rendering if the same DOM node already has children.
     * Replacing the node guarantees Seal treats it as a new widget mount point.
     *
     * Full recommended snippet per Seal docs:
     *   <div class="shopify-block shopify-app-block">
     *     <div class="sealsubs-target-element" data-seal-extension
     *          data-product-id="..." data-product="..." data-handle="..."></div>
     *   </div>
     * Ref: https://www.sealsubscriptions.com/articles/merchant-javascript-api-documentation
     */
    function loadSealWidget() {
      if (!useSeal || !state.product) {
        console.log('[SubCustomizer] loadSealWidget – skipped', {
          useSeal   : useSeal,
          hasProduct: !!state.product
        });
        return;
      }

      // Double-call guard
      var loadKey = state.product.id + ':' + (state.variant ? state.variant.id : 'no-variant');
      if (loadKey === sealLoadKey) {
        console.log('[SubCustomizer] loadSealWidget – already loading this product/variant, skipped', loadKey);
        return;
      }
      sealLoadKey = loadKey;

      // Hide placeholder, show the Seal block
      var placeholder = sealWrapper.querySelector('[data-seal-placeholder]');
      var sealBlock   = sealWrapper.querySelector('[data-seal-block]');
      if (placeholder) placeholder.style.display = 'none';
      if (sealBlock)   sealBlock.style.display    = '';

      // Replace the target element with a fresh node so Seal's MutationObserver fires
      var freshTarget = document.createElement('div');
      freshTarget.className = 'sealsubs-target-element';
      freshTarget.setAttribute('data-seal-extension', '');
      freshTarget.setAttribute('data-handle', state.product.handle || '');
      freshTarget.setAttribute('data-product-id', String(state.product.id || ''));
      try {
        freshTarget.setAttribute('data-product', JSON.stringify(state.product));
      } catch (e) {}
      if (state.variant) {
        freshTarget.setAttribute('data-variant-id', String(state.variant.id));
      }

      if (sealTarget && sealTarget.parentNode) {
        sealTarget.parentNode.replaceChild(freshTarget, sealTarget);
      } else if (sealBlock) {
        sealBlock.appendChild(freshTarget);
      }
      sealTarget = freshTarget; // update live reference

      console.log('[SubCustomizer] loadSealWidget – fresh target inserted', {
        handle   : state.product.handle,
        productId: state.product.id,
        variantId: state.variant ? state.variant.id : null,
        element  : sealTarget
      });

      callSealRefresh();
    }

    /** Call SealSubs.refresh(), retrying if not yet available. Runs a post-load check. */
    function callSealRefresh() {
      var productHandle = state.product ? state.product.handle : '(none)';

      var doRefresh = function () {
        console.log('[SubCustomizer] ► SealSubs.refresh() – product:', productHandle);
        window.SealSubs.refresh();

        // Post-load diagnostic: 2 s after refresh, check whether Seal injected content
        setTimeout(function () {
          if (!sealTarget) return;
          var kids = sealTarget.children.length;
          console.log('[SubCustomizer] post-refresh check (2 s)', {
            childrenInTarget: kids,
            handle          : sealTarget.getAttribute('data-handle'),
            innerHTML_preview: sealTarget.innerHTML.substring(0, 120)
          });
          if (kids === 0) {
            console.warn(
              '[SubCustomizer] ✗ Seal target still empty 2 s after refresh.\n' +
              '  Diagnostics to try in browser console:\n' +
              '    1. document.querySelectorAll(".sealsubs-target-element") — should find 1 element with data-handle set\n' +
              '    2. window.SealSubs.refresh() — run manually, see if widget appears\n' +
              '    3. Check Seal app → Subscription rules → confirm "' + productHandle + '" is in a rule\n' +
              '    4. Check App embeds: Shopify Admin → Online Store → Themes → Customize → App embeds → Seal Subscriptions ON'
            );
          }
        }, 2000);
      };

      if (typeof window.SealSubs !== 'undefined' && typeof window.SealSubs.refresh === 'function') {
        console.log('[SubCustomizer] SealSubs ready – calling refresh immediately');
        doRefresh();
        return;
      }

      console.log('[SubCustomizer] SealSubs not ready – polling every 300 ms (up to 9 s)…');
      var attempts = 0;
      var timer = setInterval(function () {
        attempts++;
        if (typeof window.SealSubs !== 'undefined' && typeof window.SealSubs.refresh === 'function') {
          clearInterval(timer);
          console.log('[SubCustomizer] SealSubs ready after', attempts, 'poll(s) – calling refresh');
          doRefresh();
          return;
        }
        if (attempts >= 30) {
          clearInterval(timer);
          console.warn('[SubCustomizer] ✗ SealSubs did not load after 9 s. Enable app embed: Shopify Admin → Online Store → Themes → Customize → App embeds → Seal Subscriptions.');
        }
      }, 300);
    }

    /** Show placeholder, hide/reset the Seal block. Call on init or when coffee is deselected. */
    function clearSealWidget() {
      sealLoadKey = null;
      var placeholder = sealWrapper && sealWrapper.querySelector('[data-seal-placeholder]');
      var sealBlock   = sealWrapper && sealWrapper.querySelector('[data-seal-block]');
      if (placeholder) placeholder.style.display = '';
      if (sealBlock)   sealBlock.style.display    = 'none';
    }

    // ── Seal events ─────────────────────────────────────────────────────
    // Per docs: listen on document, scope to this section by checking e.detail.element

    if (useSeal || appBlocks.length) {
      function sealEventBelongsToSection(e) {
        if (!e.detail || !e.detail.element) return false;
        return sectionEl.contains(e.detail.element);
      }

      document.addEventListener('sealsubs:subscription_widget_created', function (e) {
        if (!sealEventBelongsToSection(e)) return;
        console.log('[SubCustomizer] ✓ sealsubs:subscription_widget_created – Seal widget is ready');
        var initialPlanInput = e.detail.element.querySelector('input[name="selling_plan"]');
        syncSellingPlanState(initialPlanInput ? initialPlanInput.value : '', '');
        updateForm();
        updateSummary();
      });

      document.addEventListener('sealsubs:selling_plan_changed', function (e) {
        if (!sealEventBelongsToSection(e)) return;
        var planId = e.detail.selling_plan_id ? String(e.detail.selling_plan_id) : '';
        console.log('[SubCustomizer] sealsubs:selling_plan_changed', { sellingPlanId: planId });
        syncSellingPlanState(planId, e.detail.selling_plan_name || e.detail.selling_plan_label || '');
        updateForm();
        updateSummary();
      });

      document.addEventListener('sealsubs:price_update', function (e) {
        if (!sealEventBelongsToSection(e)) return;
        console.log('[SubCustomizer] sealsubs:price_update', {
          price          : e.detail.price,
          compareAtPrice : e.detail.compareAtPrice
        });
        updateSummary();
      });
    }

    // ── Grind options ───────────────────────────────────────────────────

    function renderGrindOptions(product) {
      if (!grindSelect) return;
      
      grindSelect.innerHTML = '';
      
      var placeholder = document.createElement('option');
      placeholder.value = "";
      placeholder.disabled = true;
      placeholder.textContent = "Choose Bean Grind";
      grindSelect.appendChild(placeholder);

      if (product && product.grind_options && product.grind_options.length) {
        product.grind_options.forEach(function(opt, i) {
          var option = document.createElement('option');
          option.value = opt;
          option.textContent = opt;
          if (i === 0) {
            option.selected = true;
          }
          grindSelect.appendChild(option);
        });
      } else {
        var option = document.createElement('option');
        option.value = "Whole Bean";
        option.textContent = "Whole Bean";
        option.selected = true;
        grindSelect.appendChild(option);
      }
    }

    // ── Size pills ──────────────────────────────────────────────────────

    function renderSizePills(product) {
      if (!sizeList) return;
      sizeList.innerHTML = '';
      var placeholder = sizeList.getAttribute('data-size-placeholder-text') || 'Select a coffee above';
      var sizeOpt = findSizeOption(product);
      if (sizeOpt && sizeOpt.values && sizeOpt.values.length) {
        sizeOpt.values.forEach(function (val, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'subscription-customizer__size-pill' + (i === 0 ? ' is-selected' : '');
          btn.setAttribute('data-size-pill', '');
          btn.setAttribute('data-size-value', val);
          btn.textContent = val;
          sizeList.appendChild(btn);
        });
      } else {
        var span = document.createElement('span');
        span.className = 'subscription-customizer__size-placeholder body-medium';
        span.setAttribute('data-size-placeholder', '');
        span.textContent = placeholder;
        sizeList.appendChild(span);
      }
    }

    // ── Helpers ─────────────────────────────────────────────────────────

    function getSelectedSize() {
      var pill = sizeList && sizeList.querySelector('[data-size-pill].is-selected');
      return pill ? (pill.getAttribute('data-size-value') || '') : '';
    }

    function getSelectedGrind() {
      return grindSelect ? (grindSelect.value || '') : '';
    }

    function getGrindOptionFromVariant(product, variant) {
      if (!product || !variant) return '';
      var opts = product.options || [];
      var grindIdx = -1;
      for (var i = 0; i < opts.length; i++) {
        if ((opts[i].name || '').toLowerCase() === 'grind') grindIdx = i;
      }
      if (grindIdx < 0) return '';
      var o = [
        (variant.option1 || '').trim(),
        (variant.option2 || '').trim(),
        (variant.option3 || '').trim()
      ];
      return o[grindIdx] || '';
    }

    function stripLeadingStepNumber(text) {
      var t = (text || '').trim();
      var stripped = t.replace(/^\s*\d+\.\s*/, '').trim();
      if (stripped !== t) return stripped;
      return t.replace(/^\s*\d+\./, '').trim();
    }

    /** When grind step exists but is hidden (5 lb), show step 4 on frequency; otherwise step 5. */
    function updateFrequencyStepNumberLabel() {
      if (!grindStepEl || !frequencyHeadingEl) return;
      if (!frequencyHeadingEl.dataset.subCustomizerFreqHeadingOrig) {
        frequencyHeadingEl.dataset.subCustomizerFreqHeadingOrig = frequencyHeadingEl.textContent.trim();
      }
      var orig = frequencyHeadingEl.dataset.subCustomizerFreqHeadingOrig;
      var rest = stripLeadingStepNumber(orig);
      if (!rest) rest = orig;
      var hideGrind = !!(state.product && isFiveLbSize(getSelectedSize()));
      var num = hideGrind ? 4 : 5;
      var label = num + '. ' + rest;
      frequencyHeadingEl.textContent = label;
      if (frequencyList) frequencyList.setAttribute('aria-label', label);
    }

    function updateGrindStepVisibility() {
      if (!grindStepEl) return;
      var size = getSelectedSize();
      var hide = !!(state.product && isFiveLbSize(size));
      grindStepEl.style.display = hide ? 'none' : '';
      updateFrequencyStepNumberLabel();
    }

    function resolveVariant() {
      if (!state.product) {
        state.variant = null;
        return;
      }
      var size = getSelectedSize();
      var grind = getSelectedGrind();
      if (isFiveLbSize(size)) {
        state.variant =
          findVariant(state.product, size, 'Whole Bean') ||
          findVariant(state.product, size, '');
      } else {
        state.variant = findVariant(state.product, size, grind);
      }
    }

    function getSellingPlanNameFromVariant(planId) {
      if (!planId || !state.variant || !state.variant.selling_plan_allocations) return '';
      for (var i = 0; i < state.variant.selling_plan_allocations.length; i++) {
        var alloc = state.variant.selling_plan_allocations[i];
        if (String(alloc.selling_plan_id) === String(planId)) {
          return alloc.name || '';
        }
      }
      return '';
    }

    function syncSellingPlanState(planId, planName) {
      state.sellingPlanId = planId ? String(planId) : '';
      if (!state.sellingPlanId) {
        state.frequencyLabel = 'One-time purchase';
        return;
      }
      state.frequencyLabel = planName || getSellingPlanNameFromVariant(state.sellingPlanId) || 'Subscription';
    }

    // ── Form + summary ──────────────────────────────────────────────────

    function updateForm() {
      if (!form || !variantInput || !qtyFormInput) return;
      var sellingPlanEl = form.querySelector('[data-form-selling-plan]');
      if (grindFormInput) {
        if (isFiveLbSize(getSelectedSize()) && state.variant) {
          grindFormInput.value = getGrindOptionFromVariant(state.product, state.variant) || '';
        } else if (grindSelect && grindSelect.selectedIndex >= 0) {
          grindFormInput.value = grindSelect.options[grindSelect.selectedIndex].text || '';
        } else {
          grindFormInput.value = '';
        }
      }
      if (state.variant && state.variant.available) {
        variantInput.value = state.variant.id;
        qtyFormInput.value = state.quantity;
        if (sellingPlanEl) sellingPlanEl.value = state.sellingPlanId || '';
        if (submitBtn) submitBtn.disabled = false;
      } else {
        variantInput.value = '';
        qtyFormInput.value = '1';
        if (sellingPlanEl) sellingPlanEl.value = '';
        if (submitBtn) submitBtn.disabled = true;
      }
    }

    function updateSummary() {
      function set(sel, txt) {
        var el = sectionEl.querySelector(sel);
        if (el) el.textContent = txt;
      }
      var sizePill   = sizeList && sizeList.querySelector('[data-size-pill].is-selected');
      var sizeLabel  = sizePill ? sizePill.textContent.trim() : '';
      var grindLabel = '';
      if (isFiveLbSize(getSelectedSize()) && state.variant) {
        grindLabel = getGrindOptionFromVariant(state.product, state.variant) || '';
      } else if (grindSelect && grindSelect.selectedIndex >= 0) {
        grindLabel = grindSelect.options[grindSelect.selectedIndex].text || '';
      }

      set('[data-summary-coffee]',    state.product ? state.product.title : '—');
      set('[data-summary-size]',      sizeLabel  || '—');
      set('[data-summary-quantity]',  state.quantity + (state.quantity === 1 ? ' Piece' : ' Pieces'));
      set('[data-summary-grind]',     grindLabel || '—');
      set('[data-summary-frequency]', state.frequencyLabel || '—');

      var discountWrap = sectionEl.querySelector('[data-summary-discount-wrap]');
      if (discountWrap) discountWrap.style.display = 'none';

      if (state.variant) {
        var price = state.variant.price;
        var allocs = state.variant.selling_plan_allocations || [];
        for (var i = 0; i < allocs.length; i++) {
          if (String(allocs[i].selling_plan_id) === String(state.sellingPlanId)) {
            price = allocs[i].price;
            break;
          }
        }
        var total = price * state.quantity;
        set('[data-summary-subtotal]', formatMoney(total));
        set('[data-summary-total]',    formatMoney(total));
      } else {
        set('[data-summary-subtotal]', '—');
        set('[data-summary-total]',    '—');
      }
    }

    // ── App block show / hide ─────────────────────────────────────────────
    /**
     * Hide every [data-app-block] element, then show the one whose inner
     * .sealsubs-target-element matches the selected product (by data-product-id
     * or data-handle). If Seal hasn't injected content yet (widget was hidden
     * during page load), call SealSubs.refresh() to trigger rendering.
     */
    function showAppBlock(product) {
      if (!appBlocks.length) return;

      var matched = null;

      appBlocks.forEach(function (blockEl) {
        blockEl.style.display = 'none';

        if (!product || matched) return;

        // Seal's app block renders a .sealsubs-target-element with data-product-id / data-handle
        var sealEl = blockEl.querySelector('.sealsubs-target-element') ||
                     blockEl.querySelector('[data-product-id]') ||
                     blockEl.querySelector('[data-handle]');

        if (!sealEl) {
          console.log('[SubCustomizer] app block has no identifiable product element', blockEl);
          return;
        }

        var blockProductId = (sealEl.getAttribute('data-product-id') || '').trim();
        var blockHandle    = (sealEl.getAttribute('data-handle') || '').trim();

        var matchById     = blockProductId && String(blockProductId) === String(product.id);
        var matchByHandle = blockHandle    && blockHandle === product.handle;

        if (matchById || matchByHandle) {
          matched = { blockEl: blockEl, sealEl: sealEl };
        }
      });

      if (!matched) {
        console.log('[SubCustomizer] showAppBlock – no app block found for product', {
          id: product.id, handle: product.handle
        });
        return;
      }

      matched.blockEl.style.display = '';
      console.log('[SubCustomizer] showAppBlock – showing block for', product.handle, matched.blockEl);

      // If Seal hasn't rendered inside this block yet (was hidden on page load), refresh now
      if (matched.sealEl.children.length === 0) {
        console.log('[SubCustomizer] app block target is empty – calling SealSubs.refresh()');
        if (typeof window.SealSubs !== 'undefined' && typeof window.SealSubs.refresh === 'function') {
          window.SealSubs.refresh();
        } else {
          // Wait for SealSubs to load
          var attempts = 0;
          var timer = setInterval(function () {
            attempts++;
            if (typeof window.SealSubs !== 'undefined' && typeof window.SealSubs.refresh === 'function') {
              clearInterval(timer);
              console.log('[SubCustomizer] SealSubs ready after', attempts, 'polls – calling refresh for app block');
              window.SealSubs.refresh();
            } else if (attempts >= 30) {
              clearInterval(timer);
              console.warn('[SubCustomizer] SealSubs did not load after 9 s.');
            }
          }, 300);
        }
      }
    }

    // ── Event handlers ───────────────────────────────────────────────────

    function onCoffeeSelect(card) {
      coffeeList.querySelectorAll('[data-coffee-card]').forEach(function (c) {
        c.classList.remove('is-selected');
      });
      card.classList.add('is-selected');

      var productId = card.getAttribute('data-product-id');
      state.product = null;
      for (var i = 0; i < products.length; i++) {
        if (String(products[i].id) === String(productId)) {
          state.product = products[i];
          break;
        }
      }
      console.log('[SubCustomizer] Coffee selected:', state.product ? state.product.handle : '(not found)');
      console.log('[SubCustomizer] Available grind options:', state.product ? state.product.grind_options : 'none');

      renderGrindOptions(state.product);
      renderSizePills(state.product);
      updateGrindStepVisibility();
      resolveVariant();

      // Reset selling plan state for new product
      state.sellingPlanId  = '';
      state.frequencyLabel = '';

      updateForm();
      updateSummary();

      // App blocks: hide all, show the one matching this product
      showAppBlock(state.product);

      // Manual Seal widget (separate from app blocks)
      if (useSeal) {
        sealLoadKey = null; // reset so new product always loads fresh
        loadSealWidget();
      }
    }

    function onSizeSelect(pill) {
      sizeList.querySelectorAll('[data-size-pill]').forEach(function (p) {
        p.classList.remove('is-selected');
      });
      pill.classList.add('is-selected');
      updateGrindStepVisibility();
      resolveVariant();
      updateForm();
      updateSummary();
      // Re-load Seal widget to reflect new variant
      if (useSeal && state.product) {
        sealLoadKey = null;
        loadSealWidget();
      }
    }

    function onGrindChange() {
      resolveVariant();
      updateForm();
      updateSummary();
      if (useSeal && state.product) {
        sealLoadKey = null;
        loadSealWidget();
      }
    }

    function onQuantityChange() {
      var val = parseInt(quantityInput.value, 10);
      state.quantity = isNaN(val) ? 1 : Math.min(Math.max(val, 1), 99);
      quantityInput.value = state.quantity;
      updateForm();
      updateSummary();
    }

    // ── Bind events ─────────────────────────────────────────────────────

    if (coffeeList) {
      coffeeList.addEventListener('click', function (e) {
        var card = e.target.closest('[data-coffee-card]');
        if (!card) return;
        e.preventDefault();
        onCoffeeSelect(card);
      });
    }

    if (sizeList) {
      sizeList.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-size-pill]');
        if (!pill) return;
        onSizeSelect(pill);
      });
    }

    if (grindSelect) {
      grindSelect.addEventListener('change', onGrindChange);
    }

    if (quantityInput) {
      quantityInput.addEventListener('input',  onQuantityChange);
      quantityInput.addEventListener('change', onQuantityChange);
    }

    var viewMoreTrigger = sectionEl.querySelector('[data-view-more-trigger]');
    if (viewMoreTrigger && coffeeList) {
      viewMoreTrigger.addEventListener('click', function () {
        var expanded = coffeeList.classList.toggle('is-expanded');
        viewMoreTrigger.setAttribute('aria-expanded', String(expanded));
        var textEl = viewMoreTrigger.querySelector('.subscription-customizer__view-more-text');
        if (textEl) {
          textEl.textContent = expanded
            ? (viewMoreTrigger.getAttribute('data-view-less-text') || 'View less')
            : (viewMoreTrigger.getAttribute('data-view-more-text') || 'View more beans');
        }
      });
    }

    // Theme frequency radios (only when Seal is off)
    if (!useSeal && frequencyList) {
      frequencyList.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          state.sellingPlanId  = radio.value || '';
          state.frequencyLabel = radio.getAttribute('data-frequency-label') || '';
          updateForm();
          updateSummary();
        });
        // Pick up initial checked state
        if (radio.checked) {
          state.sellingPlanId  = radio.value || '';
          state.frequencyLabel = radio.getAttribute('data-frequency-label') || '';
        }
      });
    }

    // ── Form submit ─────────────────────────────────────────────────────
    // Per docs: "When you add the product to the cart, make sure to include
    // values of all input elements from the subscription widget in the request."

    if (form && submitBtn) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!state.variant || !state.variant.available) return;

        var sellingPlanEl = form.querySelector('[data-form-selling-plan]');

        if (useSeal && sealWrapper) {
          // Prefer the selling_plan input directly inside the Seal widget
          var sealPlanInput = sealWrapper.querySelector('input[name="selling_plan"]');
          var planValue = (sealPlanInput && sealPlanInput.value.trim())
            ? sealPlanInput.value.trim()
            : (state.sellingPlanId || '');

          if (sellingPlanEl) {
            sellingPlanEl.setAttribute('name', 'selling_plan');
            sellingPlanEl.value = planValue;
          }

          // Clone any other Seal inputs (e.g. recurring invoice line item properties)
          form.querySelectorAll('[data-seal-clone]').forEach(function (el) { el.remove(); });
          sealWrapper.querySelectorAll('input, select').forEach(function (inp) {
            if (!inp.name || inp.name === 'selling_plan') return;
            if ((inp.type === 'radio' || inp.type === 'checkbox') && !inp.checked) return;
            var hidden = document.createElement('input');
            hidden.type  = 'hidden';
            hidden.name  = inp.name;
            hidden.value = inp.value || '';
            hidden.setAttribute('data-seal-clone', '');
            form.appendChild(hidden);
          });

          console.log('[SubCustomizer] submit – selling_plan:', planValue);
        } else if (sellingPlanEl) {
          sellingPlanEl.setAttribute('name', 'selling_plan');
        }

        var submitText = submitBtn.querySelector('.btn__text');
        var origText   = submitText ? submitText.textContent : '';
        if (submitText) submitText.textContent = 'Adding…';
        submitBtn.disabled = true;

        var addUrl = form.action.indexOf('.js') > -1
          ? form.action
          : form.action.replace(/\/cart\/add\/?$/, '/cart/add.js');

        fetch(addUrl, {
          method  : 'POST',
          body    : new FormData(form),
          headers : { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (r) {
            return r.json().then(function (d) { return { ok: r.ok, data: d }; });
          })
          .then(function (result) {
            if (result.ok && !result.data.message) {
              window.location.href = '/checkout';
            } else {
              if (submitText) submitText.textContent = origText;
              submitBtn.disabled = false;
              alert((result.data && result.data.description) || 'Unable to add to cart. Please try again.');
            }
          })
          .catch(function () {
            if (submitText) submitText.textContent = origText;
            submitBtn.disabled = false;
            alert('Something went wrong. Please try again.');
          });
      });
    }

    // ── Initial render ───────────────────────────────────────────────────
    // No coffee selected on page load

    state.quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;
    renderSizePills(null);       // shows "Select a coffee above" placeholder
    updateGrindStepVisibility();
    if (useSeal) clearSealWidget(); // shows "Select a coffee above to choose your frequency."
    updateForm();
    updateSummary();
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-section-type="subscription-customizer"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      e.target.querySelectorAll('[data-section-type="subscription-customizer"]').forEach(initSection);
    });
  }
})();
